import React from "react";
import PropTypes from "prop-types";

import { intervalNote, unitLabel } from "../helper/rxData";

/**
 * Card · Follow-up — Figma 3031:9832 ("variation B (days/weeks)").
 *
 * INTERVAL ONLY (Nittin, 2026-07-27): the old "date" variant, its time-slot
 * picker and the Follow-up-type switch between them are gone. A follow-up is
 * expressed as "follow up after N days/weeks"; the date is derived from the
 * prescription date (today), and the slot is chosen when the appointment is booked.
 *
 * Presentational: `value` is the INITIAL_FOLLOW_UP shape and every edit calls
 * `onChange` with the whole merged object, since the follow-up line in the
 * document is derived from count + unit together.
 */
/**
 * The two intervals, LABEL separate from VALUE on purpose.
 *
 * The label is what the doctor reads — "Day/s", covering both the singular and
 * the plural without an awkward "1 Days". The value is "Days"/"Weeks" and must
 * NOT follow it, because the value is not a label: it is lower-cased straight
 * into the sentence the prescription prints ("Follow-up is suggested after 2
 * weeks", rxData.js), it is sent on the wire as `followUp.unit`
 * (rxMappers followUpBody), and it is parked in the builder's scratchpad. A
 * value of "Week/s" would put "after 2 week/s" on a signed document and change
 * a stored field's content for every follow-up ever written.
 */
const UNITS = ["Days", "Weeks"].map((value) => ({ value, label: unitLabel(value) }));

const FollowUpCard = ({ value, onChange }) => {
  const set = (patch) => onChange({ ...value, ...patch });

  // Count is a plain text input (numeric) — the tiny arrows are the stepper, and
  // it never drops below 1: "follow up after 0 weeks" is not a follow-up.
  const setCount = (n) => set({ count: Math.max(1, Math.min(n, 99)) });

  return (
    <section className="rx-card">
      <header className="rx-card-head">
        <h3 className="rx-card-title">Follow-up</h3>
        <span className="rx-card-meta">
          · appears under “Other Instructions” in the prescription
        </span>
      </header>

      <div className="rx-field">
        <label className="rx-field-label" htmlFor="rx-fu-count">
          Follow up after
        </label>
        <div className="rx-fu-interval">
          <span className="rx-field-wrap rx-stepper">
            <input
              id="rx-fu-count"
              type="text"
              inputMode="numeric"
              className="rx-input"
              value={String(value.count)}
              onChange={(e) => setCount(Number(e.target.value.replace(/[^0-9]/g, "")) || 1)}
            />
            <span className="rx-stepper-arrows">
              <button type="button" onClick={() => setCount(value.count + 1)} aria-label="Increase">
                &#9652;
              </button>
              <button type="button" onClick={() => setCount(value.count - 1)} aria-label="Decrease">
                &#9662;
              </button>
            </span>
          </span>

          <div className="rx-seg" role="group" aria-label="Interval unit">
            {UNITS.map((u) => (
              <button
                key={u.value}
                type="button"
                className={`rx-seg-cell${value.unit === u.value ? " is-on" : ""}`}
                aria-pressed={value.unit === u.value}
                onClick={() => set({ unit: u.value })}
              >
                {u.label}
              </button>
            ))}
          </div>
        </div>
        <p className="rx-fu-note">
          {intervalNote(value.count, value.unit)}
          {value.scheduled ? " Added to the prescription." : ""}
        </p>
      </div>

      {/* Figma 3031:9848 — right-aligned pair, Cancel first, 10px apart. The
          card has no other scheduled-state affordance: no confirmation chip, no
          label swap, no colour change. Cancel's presence IS the state.
          One deliberate divergence: the primary is disabled once scheduled,
          because the line is already in the prescription and re-clicking would
          do nothing (the document tracks count/unit live). */}
      <div className="rx-card-foot is-end">
        {value.scheduled && (
          <button type="button" className="rx-btn is-white" onClick={() => set({ scheduled: false })}>
            Cancel
          </button>
        )}
        <button
          type="button"
          className="rx-btn is-ghost-purple"
          disabled={value.scheduled}
          onClick={() => set({ scheduled: true })}
        >
          Schedule follow-up
        </button>
      </div>
    </section>
  );
};
FollowUpCard.propTypes = {
  value: PropTypes.shape({
    count: PropTypes.number.isRequired,
    unit: PropTypes.string.isRequired,
    /** In the prescription? Only true once "Schedule follow-up" is clicked. */
    scheduled: PropTypes.bool,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default FollowUpCard;
